import { morph } from '@theme/morph';

// Medium and Small are separate products, so the size rows in
// blocks/skullette-size-switcher.liquid cannot be a Shopify option. This
// module makes the hop feel like one: fetch the sibling page, morph <main>,
// push its URL. Same move Horizon makes for combined-listing children
// (assets/variant-picker.js updateMain) - reusing it keeps one swap
// behaviour on the PDP instead of two.
//
// Any failure falls back to a plain navigation: a slow swap is a bad tap, a
// dead radio is a lost sale.

/** @type {Map<string, Promise<string>>} in-flight and settled page fetches */
const pages = new Map();

/** Whether this document has swapped product at least once. */
let hasSwapped = false;

/**
 * @param {string} url
 * @returns {Promise<string>}
 */
function fetchPage(url) {
  let pending = pages.get(url);
  if (!pending) {
    // A rejected entry must not stick around: the next tap should retry the
    // fetch rather than replay the failure and go straight to a hard reload.
    pending = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`${response.status}`);
        return response.text();
      })
      .catch((error) => {
        pages.delete(url);
        throw error;
      });
    pages.set(url, pending);
  }
  return pending;
}

/**
 * Swaps the page over to the sibling size.
 * @param {string} url - The sibling product URL.
 * @param {HTMLElement} group - The size fieldset, used for the busy state.
 */
async function switchSize(url, group) {
  group.setAttribute('aria-busy', 'true');

  try {
    const html = await fetchPage(url);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const main = document.querySelector('main');
    const newMain = doc.querySelector('main');
    if (!main || !newMain) throw new Error('no main');

    morph(main, newMain);
    hasSwapped = true;
    history.pushState({ skSize: url }, '', url);
    if (doc.title) document.title = doc.title;

    // The morph replaces the nodes the Skullette PDP module bound its
    // listeners to. Set picker and ship manifest re-arm themselves through
    // their own observers; the PDP module needs telling.
    document.dispatchEvent(new CustomEvent('skullette:pdp:rebind'));
  } catch {
    window.location.assign(url);
  } finally {
    // The morph brings in a fresh fieldset, so this only matters when the
    // swap failed before replacing anything.
    group.removeAttribute('aria-busy');
  }
}

/**
 * @param {HTMLElement} group
 */
function initGroup(group) {
  if (/** @type {any} */ (group).__skSizeInit) return;
  /** @type {any} */ (group).__skSizeInit = true;

  group.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const url = input.dataset.sizeUrl;
    if (url) switchSize(url, group);
  });

  // Warm the sibling page while the finger is still travelling; by the time
  // the radio fires the morph usually has its HTML already.
  for (const input of group.querySelectorAll('input[data-size-url]')) {
    const url = /** @type {HTMLInputElement} */ (input).dataset.sizeUrl;
    if (!url) continue;
    const warm = () => fetchPage(url).catch(() => {});
    input.closest('label')?.addEventListener('pointerenter', warm, { once: true });
    input.addEventListener('focus', warm, { once: true });
  }
}

const scan = () => document.querySelectorAll('[data-size-switch]').forEach(initGroup);
scan();
// A size swap (and any variant morph) brings in a new fieldset - re-arm it.
new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });

// Back/forward across a swapped URL: reload rather than reverse the morph, so
// the restored page is the real server render and not a half-rebuilt DOM.
// Keyed on "this document has swapped", not on the popped state - stepping
// BACK to the first product pops the entry that existed before any swap, and
// that entry carries no marker of its own.
window.addEventListener('popstate', () => {
  if (hasSwapped) window.location.reload();
});
